/**
 * Clerk bridge — wraps @clerk/clerk-js so Elm can drive auth via ports
 * without touching JS state directly.
 *
 * Elm sends imperative commands (`signIn`, `signUp`, `verifyEmail`,
 * `signOut`, `updateUser`, `setMetadata`); Clerk maintains the session in
 * an HTTP-only cookie. After every Clerk lifecycle event we emit a normalized
 * `SessionSnapshot` back to Elm so the model stays in sync.
 */

import { Clerk } from "@clerk/clerk-js";

type ClerkCmdKind =
  | "signIn"
  | "signUp"
  | "verifyEmail"
  | "resetStart"
  | "resetConfirm"
  | "signOut"
  | "updateUser"
  | "setMetadata";

type ClerkCmd = {
  readonly kind: ClerkCmdKind;
  readonly payload: unknown;
};

type SignInPayload = {
  readonly identifier: string;
  readonly password: string;
};

type SignUpPayload = {
  readonly firstName: string;
  readonly emailAddress: string;
  readonly password: string;
};

type VerifyEmailPayload = { readonly code: string };

type ResetStartPayload = { readonly identifier: string };

type ResetConfirmPayload = {
  readonly code: string;
  readonly password: string;
};

type UpdateUserPayload = {
  readonly firstName?: string;
  readonly lastName?: string;
};

type Metadata = Record<string, unknown>;

type SessionSnapshot = {
  readonly user: {
    readonly id: string;
    readonly email: string;
    readonly firstName: string | null;
  } | null;
  readonly session: boolean;
};

type ClerkEvent =
  | { readonly kind: "signupPrepared" }
  | { readonly kind: "signupComplete" }
  | { readonly kind: "resetPrepared" }
  | { readonly kind: "resetComplete" }
  | { readonly kind: "error"; readonly cmd: ClerkCmdKind; readonly message: string };

type Ports = {
  readonly clerkCmd?: { readonly subscribe: (h: (cmd: ClerkCmd) => void) => void } | undefined;
  readonly clerkSub?: { readonly send: (s: SessionSnapshot) => void } | undefined;
  readonly clerkEvent?: { readonly send: (e: ClerkEvent) => void } | undefined;
};

const humanizeClerkError = (err: unknown): string => {
  // Clerk errors look like { errors: [{ code, message, longMessage }] }
  if (typeof err === "object" && err !== null && "errors" in err) {
    const errors = (err as { errors: unknown }).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0];
      if (typeof first === "object" && first !== null) {
        const long = (first as Record<string, unknown>)["longMessage"];
        if (typeof long === "string" && long.length > 0) return long;
        const msg = (first as Record<string, unknown>)["message"];
        if (typeof msg === "string" && msg.length > 0) return msg;
      }
    }
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
};

const isSignIn = (p: unknown): p is SignInPayload =>
  typeof p === "object" &&
  p !== null &&
  typeof (p as Record<string, unknown>)["identifier"] === "string" &&
  typeof (p as Record<string, unknown>)["password"] === "string";

const isSignUp = (p: unknown): p is SignUpPayload =>
  typeof p === "object" &&
  p !== null &&
  typeof (p as Record<string, unknown>)["firstName"] === "string" &&
  typeof (p as Record<string, unknown>)["emailAddress"] === "string" &&
  typeof (p as Record<string, unknown>)["password"] === "string";

const isVerifyEmail = (p: unknown): p is VerifyEmailPayload =>
  typeof p === "object" &&
  p !== null &&
  typeof (p as Record<string, unknown>)["code"] === "string";

const isResetStart = (p: unknown): p is ResetStartPayload =>
  typeof p === "object" &&
  p !== null &&
  typeof (p as Record<string, unknown>)["identifier"] === "string";

const isResetConfirm = (p: unknown): p is ResetConfirmPayload =>
  typeof p === "object" &&
  p !== null &&
  typeof (p as Record<string, unknown>)["code"] === "string" &&
  typeof (p as Record<string, unknown>)["password"] === "string";

const isUpdateUser = (p: unknown): p is UpdateUserPayload =>
  typeof p === "object" && p !== null;

const isMetadata = (p: unknown): p is Metadata =>
  typeof p === "object" && p !== null;

const buildSnapshot = (clerk: Clerk): SessionSnapshot => {
  const user = clerk.user;
  const session = clerk.session;
  if (!user || !session) {
    return { user: null, session: false };
  }
  return {
    user: {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? "",
      firstName: user.firstName,
    },
    session: true,
  };
};

export const wireClerkBridge = async (
  publishableKey: string,
  ports: Ports,
): Promise<void> => {
  if (ports.clerkCmd === undefined || ports.clerkSub === undefined) return;

  if (publishableKey === "" || publishableKey === "pk_test_replace_me") {
    console.warn("[clerk-bridge] no publishable key — auth is disabled");
    queueMicrotask(() => ports.clerkSub?.send({ user: null, session: false }));
    ports.clerkCmd.subscribe((cmd) => {
      console.warn(`[clerk-bridge] ignored ${cmd.kind} (no key)`, cmd);
    });
    return;
  }

  const clerk = new Clerk(publishableKey);
  await clerk.load();

  const emit = (): void => {
    ports.clerkSub?.send(buildSnapshot(clerk));
  };

  clerk.addListener(() => {
    emit();
  });

  emit();

  ports.clerkCmd.subscribe(async (cmd) => {
    try {
      switch (cmd.kind) {
        case "signIn": {
          if (!isSignIn(cmd.payload)) throw new Error("malformed signIn payload");
          const client = clerk.client;
          if (!client) throw new Error("clerk client not loaded");
          const signIn = await client.signIn.create({
            identifier: cmd.payload.identifier,
            password: cmd.payload.password,
          });
          if (signIn.status === "complete" && signIn.createdSessionId !== null) {
            await clerk.setActive({ session: signIn.createdSessionId });
          } else {
            ports.clerkEvent?.send({
              kind: "error",
              cmd: "signIn",
              message: "We couldn't complete sign-in. Please try again.",
            });
          }
          break;
        }
        case "signUp": {
          if (!isSignUp(cmd.payload)) throw new Error("malformed signUp payload");
          const client = clerk.client;
          if (!client) throw new Error("clerk client not loaded");
          const signUp = await client.signUp.create({
            firstName: cmd.payload.firstName,
            emailAddress: cmd.payload.emailAddress,
            password: cmd.payload.password,
          });
          await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
          // Tell Elm: code sent, advance to AwaitingCode stage.
          ports.clerkEvent?.send({ kind: "signupPrepared" });
          break;
        }
        case "verifyEmail": {
          if (!isVerifyEmail(cmd.payload)) throw new Error("malformed verifyEmail payload");
          const client = clerk.client;
          if (!client) throw new Error("clerk client not loaded");
          const result = await client.signUp.attemptEmailAddressVerification({
            code: cmd.payload.code,
          });
          if (result.status === "complete" && result.createdSessionId !== null) {
            await clerk.setActive({ session: result.createdSessionId });
            ports.clerkEvent?.send({ kind: "signupComplete" });
          } else {
            ports.clerkEvent?.send({
              kind: "error",
              cmd: "verifyEmail",
              message: "That code didn't work. Check your email and try again.",
            });
          }
          break;
        }
        case "resetStart": {
          if (!isResetStart(cmd.payload)) throw new Error("malformed resetStart payload");
          const client = clerk.client;
          if (!client) throw new Error("clerk client not loaded");
          await client.signIn.create({
            strategy: "reset_password_email_code",
            identifier: cmd.payload.identifier,
          });
          // Tell Elm: reset code emailed, advance to the code+password stage.
          ports.clerkEvent?.send({ kind: "resetPrepared" });
          break;
        }
        case "resetConfirm": {
          if (!isResetConfirm(cmd.payload)) throw new Error("malformed resetConfirm payload");
          const client = clerk.client;
          if (!client) throw new Error("clerk client not loaded");
          // client.signIn is the in-flight attempt created by resetStart —
          // Clerk keeps it on the client, so this works across commands.
          const attempt = await client.signIn.attemptFirstFactor({
            strategy: "reset_password_email_code",
            code: cmd.payload.code,
          });
          if (attempt.status !== "needs_new_password") {
            ports.clerkEvent?.send({
              kind: "error",
              cmd: "resetConfirm",
              message: "That code didn't work. Check your email and try again.",
            });
            break;
          }
          const done = await client.signIn.resetPassword({
            password: cmd.payload.password,
          });
          if (done.status === "complete" && done.createdSessionId !== null) {
            await clerk.setActive({ session: done.createdSessionId });
            ports.clerkEvent?.send({ kind: "resetComplete" });
          } else {
            ports.clerkEvent?.send({
              kind: "error",
              cmd: "resetConfirm",
              message: "We couldn't finish the reset. Please try again.",
            });
          }
          break;
        }
        case "signOut": {
          await clerk.signOut();
          break;
        }
        case "updateUser": {
          if (!isUpdateUser(cmd.payload)) throw new Error("malformed updateUser payload");
          await clerk.user?.update(cmd.payload);
          break;
        }
        case "setMetadata": {
          if (!isMetadata(cmd.payload)) throw new Error("malformed setMetadata payload");
          await clerk.user?.update({ unsafeMetadata: cmd.payload });
          break;
        }
      }
    } catch (err: unknown) {
      const message = humanizeClerkError(err);
      console.error(`[clerk-bridge] ${cmd.kind} failed`, err);
      ports.clerkEvent?.send({ kind: "error", cmd: cmd.kind, message });
      // Also re-emit snapshot so Elm clears any stale state.
      emit();
    }
  });
};
